import { useFlags } from "launchdarkly-react-client-sdk";
import React, { FunctionComponent, useContext } from "react";
import { useLocation } from "react-router";
import {
  AdditionalSupplementalBenefitCoverage,
  PackageServices,
  Plan,
  PlanCategoryType,
  PlanDetailsPageSection,
  Redaction,
} from "../../@types";
import routes from "../../app/routes";
import { AppStore } from "../../app/store";
import {
  CheckmarkIcon,
  PlanDetailsLink,
  StarRating,
  SuppressionHandler,
} from "../../components";
import {
  $$,
  getAdditionalBenefit,
  getBenefitCost,
  isAvailable,
  isCurrentPlan,
  isMedicareAdvantageType,
  makePlanLongId,
  planDataUtils,
  useTranslate,
} from "../../helpers";
import { calculateYourResponsibility } from "../../helpers/planHelpers";
import { getPlanNameElementId } from "./components/planHeaderHelpers";
import englishTranslations from "../../translations/en-US.json";
import {
  AnalyticsActionType,
  Ga4ParentComponentType,
} from "../../app/contexts/Analytics/types";

const Overview: FunctionComponent<{
  plans: Plan[];
  selectedPlan: Plan | undefined;
  isYearOverYear: boolean;
}> = ({ plans, selectedPlan, isYearOverYear = false }) => {
  const { mctCurrentPlanYear } = useFlags();
  const { state, dispatch } = useContext(AppStore.AppContext);
  const t = useTranslate();
  const location = useLocation();
  const BENEFIT_NOT_COVERED = "Not covered";

  const showValueOrSuppression = ({
    redactions,
    value,
    checkShouldSuppressDrugPricing = false,
  }: {
    redactions: Redaction[];
    value: string | number;
    checkShouldSuppressDrugPricing?: boolean;
  }) => (
    <SuppressionHandler
      redactions={redactions}
      suppressionWrapperProps={{ className: "ds-u-font-weight--bold" }}
      checkShouldSuppressDrugPricing={checkShouldSuppressDrugPricing}
    >
      {value}
    </SuppressionHandler>
  );

  const starRating = {
    label: t("star_rating.title"),
    getValue: function getStarRating(plan: Plan) {
      return (
        <StarRating
          hideTooltip={true}
          highPerforming={plan.high_performing}
          lowPerforming={plan.low_performing}
          stars={plan.overall_star_rating}
          planYear={plan.contract_year}
          includeLabel={false}
        />
      );
    },
  };

  const healthDeductible = {
    label: t("plan_terms.health_plan_deductible"),
    getValue: (plan: Plan) =>
      showValueOrSuppression({
        redactions: plan.redactions,
        value: plan.annual_deductible,
      }),
  };

  const drugDeductible = {
    label: t("plan_terms.drug_plan_deductible"),
    getValue: (plan: Plan) =>
      showValueOrSuppression({
        redactions: plan.redactions,
        value: planDataUtils.getAnnualDrugDeductible(
          state,
          mctCurrentPlanYear,
          plan
        ),
        checkShouldSuppressDrugPricing: true,
      }),
  };

  const oopcMax = {
    label: t("plan_terms.oopc_maximum"),
    getValue: (plan: Plan) => {
      const maxOopc = getBenefitCost(t, { plan, benefit: "maximum_oopc" });

      return showValueOrSuppression({
        redactions: plan.redactions,
        value: maxOopc,
      });
    },
  };

  const healthPremium = {
    label: t("medicare_advantage.plan_details.health_premium"),
    getValue: (plan: Plan) =>
      showValueOrSuppression({
        redactions: plan.redactions,
        value: $$(plan.partc_premium),
      }),
  };

  const drugPremium = {
    label: t("medicare_advantage.plan_details.drug_premium"),
    getValue: (plan: Plan) =>
      $$(planDataUtils.getPartDPremiumOrLIS(state, mctCurrentPlanYear, plan)),
  };

  const partBPremium = {
    label: t("plan_card.partb_premium"),
    getValue: (plan: Plan) => $$(planDataUtils.getPartBPremium(plan)),
  };

  const planFeatures = {
    label: t("plan_terms.plan_features"),
    getValue: function getPlanFeatures(plan: Plan) {
      const { package_services } = plan;
      const services = package_services || ({} as PackageServices);
      const currentPlan = isYearOverYear
        ? isCurrentPlan(state.currentCoverage, plan)
        : isCurrentPlan(state.currentCoverage, plan) ||
          isCurrentPlan(state.nextYearCoverage, plan);

      const moreBenefits = [
        {
          label: t("more_benefits.over_the_counter_drugs"),
          category: "otc_items",
          benefit: "otc_items",
        },
        {
          label: t("more_benefits.in_home_support"),
          category: "defined_supplemental_benefits",
          benefit: "in_home_support_services",
        },
        {
          label: t("more_benefits.worldwide_emergency"),
          category: "worldwide_emergency_transportation",
          benefit: "worldwide_emergency_coverage",
        },
        {
          label: t("more_benefits.worldwide_urgent"),
          category: "worldwide_emergency_transportation",
          benefit: "worldwide_urgent_care",
        },
        {
          label: t("more_benefits.response_device"),
          category: "defined_supplemental_benefits",
          benefit: "personal_emergency_response_sys_pers",
        },
        {
          label: t("more_benefits.chiropractic_service"),
          category: "chiropractic_services",
          benefit: "routine_care",
        },
        {
          label: t("more_benefits.bathroom_safety_devices"),
          category: "defined_supplemental_benefits",
          benefit: "bathroom_safety_devices",
        },
        {
          label: t("more_benefits.meals"),
          category: "meal_benefit",
          benefit: "meal_benefit",
        },
        {
          label: t("more_benefits.annual_physical"),
          category: "annual_physical_exam",
          benefit: "annual_physical_exam",
        },
        {
          label: t("more_benefits.telehealth"),
          category: "additional_telehealth_services",
          benefit: "medicare_covered_visits",
        },
      ];

      const benefitServices = [
        {
          label: t("plan_terms.comprehensive_dental.endodontics"),
          category: "comprehensive_dental",
          service: "endodontics",
        },
        {
          label: t("plan_terms.comprehensive_dental.periodontics"),
          category: "comprehensive_dental",
          service: "periodontics",
        },
        {
          label: t("plan_terms.comprehensive_dental.extractions"),
          category: "comprehensive_dental",
          service: "extractions",
        },
      ];

      return (
        <SuppressionHandler
          redactions={plan.redactions}
          suppressionWrapperProps={{ className: "ds-u-font-weight--bold" }}
        >
          <>
            <ul className="planCompare__benefitList">
              <li className="planCompare__checkItem">
                <CheckmarkIcon
                  checked={isAvailable(services.vision_services)}
                />
                <label>{t("plan_terms.vision")}</label>
              </li>

              <li className="planCompare__checkItem">
                <CheckmarkIcon
                  checked={isAvailable(services.dental_services)}
                />
                <label>{t("plan_terms.dental")}</label>
              </li>

              <li className="planCompare__checkItem">
                <CheckmarkIcon
                  checked={isAvailable(services.hearing_services)}
                />
                <label>{t("plan_terms.hearing")}</label>
              </li>

              <li className="planCompare__checkItem">
                <CheckmarkIcon
                  checked={
                    getBenefitCost(t, { benefit: "transportation", plan }) !==
                    BENEFIT_NOT_COVERED
                  }
                />
                <label>{t("plan_terms.transportation")}</label>
              </li>

              <li className="planCompare__checkItem">
                <CheckmarkIcon checked={plan.silver_sneakers} />
                <label>{t("plan_terms.fitness_benefits")}</label>
              </li>

              <li className="planCompare__checkItem">
                <CheckmarkIcon checked={plan.worldwide_emergency} />
                <label>{t("more_benefits.worldwide_emergency")}</label>
              </li>

              {moreBenefits.map(
                (benefit: {
                  label: string;
                  category: string;
                  benefit: string;
                }) => {
                  const benefitDetail = getAdditionalBenefit(
                    plan,
                    "other_benefits",
                    benefit.category,
                    benefit.benefit
                  );
                  if (benefitDetail) {
                    return (
                      <li
                        key={benefit.benefit}
                        className="planCompare__checkItem"
                      >
                        <CheckmarkIcon
                          checked={
                            benefitDetail.coverage ===
                            AdditionalSupplementalBenefitCoverage.SB_COVERAGE_SOME_COVERAGE
                          }
                        />
                        <label>{benefit.label}</label>
                      </li>
                    );
                  }
                  return null;
                }
              )}

              {benefitServices.map(
                (service: {
                  label: string;
                  category: string;
                  service: string;
                }) => {
                  const benefitDetail = getBenefitCost(t, {
                    plan,
                    benefit: service.category,
                    service: service.service,
                  });
                  if (benefitDetail) {
                    return (
                      <li
                        key={service.service}
                        className="planCompare__checkItem"
                      >
                        <CheckmarkIcon
                          checked={benefitDetail !== BENEFIT_NOT_COVERED}
                        />
                        <label>{service.label}</label>
                      </li>
                    );
                  }
                  return null;
                }
              )}
            </ul>
            <div className="planCompare__checkitem mct-hide-print">
              <PlanDetailsLink
                ariaLabelledBy={`view_bene_${plan.id} ${getPlanNameElementId(
                  plan
                )}`}
                htmlId={`view_bene_${plan.id}`}
                label={
                  <>
                    {t("plan_terms.view_additional_benefits")}
                    <span className="ds-u-visibility--screen-reader">, </span>
                  </>
                }
                plan={plan}
                onClick={() => {
                  dispatch({
                    type: AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
                    settings: {
                      linkText:
                        englishTranslations[
                          "plan_terms.view_additional_benefits"
                        ],
                      linkUrl: `${routes.planDetails.replace(
                        ":id",
                        makePlanLongId(plan)
                      )}${location.search}#${
                        PlanDetailsPageSection.EXTRA_BENEFITS
                      }`,
                      custom: {
                        parent_component_heading:
                          isYearOverYear && !currentPlan
                            ? "next year plan"
                            : "current year plan",
                        parent_component_type: Ga4ParentComponentType.TABLE,
                      },
                    },
                  });
                }}
                section={PlanDetailsPageSection.EXTRA_BENEFITS}
              />
            </div>
          </>
        </SuppressionHandler>
      );
    },
  };

  const msaYearlyDeposit = {
    label: t("msa_plan.yearly_deposit"),
    getValue: (plan: Plan) => $$(plan.msa_annual_deposit),
  };

  const msaYourResponsibility = {
    label: t("msa_plan.your_responsibility"),
    getValue: (plan: Plan) =>
      $$(
        calculateYourResponsibility(
          plan.annual_deductible,
          plan.msa_annual_deposit
        )
      ),
  };

  const monthlyPremium = {
    label: t("plan_compare.pdp.total_monthly_premium"),
    getValue: (plan: Plan) =>
      showValueOrSuppression({
        redactions: plan.redactions,
        value: $$(plan.calculated_monthly_premium),
        checkShouldSuppressDrugPricing: true,
      }),
  };

  let sections: {
    label: string;
    getValue: (plan: Plan) => JSX.Element | string | number;
  }[] = [starRating];

  if (isMedicareAdvantageType(plans[0].plan_type)) {
    if (isYearOverYear) {
      sections = [monthlyPremium, ...sections];
    }

    sections = [
      ...sections,
      healthDeductible,
      drugDeductible,
      oopcMax,
      healthPremium,
      drugPremium,
      partBPremium,
    ];

    if (plans.some(plan => plan.category === PlanCategoryType.MSA)) {
      sections = [...sections, msaYearlyDeposit, msaYourResponsibility];
    }

    sections = [...sections, planFeatures];
  } else {
    sections = [
      ...sections,
      monthlyPremium,
      {
        ...drugDeductible,
        label: t("pdp.plan_details.yearly_drug_deductible"),
      },
    ];
  }

  return (
    <>
      <tr>
        <td
          className="PlanComparePage__tableHeader ds-u-border-top--0"
          colSpan={selectedPlan ? 1 : plans.length + 1}
        >
          {t("plan_terms.overview")}
        </td>
      </tr>
      {sections.map((section, index) => {
        return (
          <tr key={index}>
            <td className="PlanComparePage__tableLabelColumn">
              <span className="PlanComparePage__tableColumnLabel">
                {section.label}
              </span>
            </td>
            {plans.map((plan, index) => (
              <td
                key={index}
                className={`${
                  selectedPlan?.id === plan.id
                    ? "PlanComparePage__selectedCell"
                    : ""
                }`}
              >
                <span className="ds-text-heading--md ds-u-display--block ds-u-lg-display--none mct-hide-print">
                  {section.label}
                </span>
                {section.getValue(plan)}
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
};

export default Overview;
